import React, { useState } from "react";
import { Video, MonitorUp, ShieldAlert, Loader2, Eye } from "lucide-react";

/** Gate shown before a proctored interview starts — must get explicit consent for webcam +
 * screen recording before any question is shown, matching real exam-proctoring UX. */
export default function ProctoringConsentScreen({ starting, error, onConsent }) {
  const [visionCoachEnabled, setVisionCoachEnabled] = useState(false);

  return (
    <div className="max-w-lg mx-auto glass-panel rounded-2xl p-8 text-center" data-testid="proctoring-consent-screen">
      <ShieldAlert className="w-10 h-10 text-amber-400 mx-auto mb-3" />
      <h2 className="text-xl font-bold text-white mb-2">Proctored Interview</h2>
      <p className="text-sm text-slate-400 mb-6">
        This interview is monitored like a real exam. By continuing, you agree to:
      </p>
      <div className="space-y-3 text-left mb-6">
        <div className="flex items-center gap-3 text-sm text-slate-300">
          <Video className="w-4 h-4 text-indigo-400 shrink-0" /> Your webcam stays on and is recorded for the full interview
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-300">
          <MonitorUp className="w-4 h-4 text-indigo-400 shrink-0" /> Your screen is shared and recorded for the full interview
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-300">
          <ShieldAlert className="w-4 h-4 text-indigo-400 shrink-0" /> Switching tabs, exiting fullscreen, or pasting text is tracked — 3 violations end the interview
        </div>
      </div>

      <label
        className="flex items-start gap-3 text-left mb-6 bg-white/[0.03] border border-white/10 rounded-xl p-4 cursor-pointer hover:border-violet-400/30 transition-colors"
        data-testid="vision-coach-optin-toggle"
      >
        <input
          type="checkbox" checked={visionCoachEnabled} onChange={(e) => setVisionCoachEnabled(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-violet-500 shrink-0" data-testid="vision-coach-optin-checkbox"
        />
        <span>
          <span className="flex items-center gap-1.5 text-sm font-semibold text-white">
            <Eye className="w-3.5 h-3.5 text-violet-400" /> Also enable AI Communication Coach
          </span>
          <span className="block text-xs text-slate-400 mt-1">
            Optional. Uses the same camera feed to give you an eye-contact & confidence score at the end — no live interruptions, just a report card. Off by default.
          </span>
        </span>
      </label>

      {error && <p className="text-sm text-rose-400 mb-4" data-testid="proctoring-consent-error">{error}</p>}
      <button
        onClick={() => onConsent(visionCoachEnabled)}
        disabled={starting}
        data-testid="proctoring-consent-start-button"
        className="w-full bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
      >
        {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {starting ? "Requesting camera & screen access…" : "Enable Camera & Screen, Start Interview"}
      </button>
    </div>
  );
}
